import React from "react";
import { CampaignStatusType } from "@/api/campaign";
import type { CampaignData, CampaignUser } from "@/api/campaign";

export const isDraft = (campaign: CampaignData | undefined) => {
	if (!campaign) return false;
	return !campaign.markets || campaign.markets.length === 0;
};

export const statusBadgeStyle = (statusType: CampaignStatusType): React.CSSProperties => {
	const base: React.CSSProperties = {
		padding: "2px 12px",
		borderRadius: 4,
		fontSize: 13,
		fontWeight: 500,
		display: "inline-block",
	};

	switch (statusType) {
		case CampaignStatusType.COMPLETE:
			return { ...base, background: "#e6f9ed", color: "#389e5a" };
		case CampaignStatusType.IN_PROGRESS:
			return { ...base, background: "#fff8e1", color: "#d4a017" };
		default:
			return { ...base, background: "#f0f0f0", color: "#888" };
	}
};

// Phase-specific badge styles (use these for phase labels/pills)
export const phaseBadgeStyle = (phase: string): React.CSSProperties => {
	const base: React.CSSProperties = {
		padding: "2px 8px 2px 8px",
		borderRadius: 4,
		fontSize: 12,
		fontWeight: 500,
		display: "inline-block",
	};

	const p = (phase || "").toLowerCase().trim();

	switch (p) {
		case "proposal":
		case "briefing":
		case "shortlisting":
			return { ...base, background: "#DEF5FC", color: "#0182AB", border: "1px solid #ABE4F6" };
		case "scripting":
		case "video production":
		case "video_production":
		case "filming":
			return { ...base, background: "#CDD4F6", color: "#233DC1" };
		case "go live":
		case "go_live":
		case "go-live":
			return { ...base, background: "#FBB7D7", color: "#DE0468" };
		case "completed":
		case "complete":
			return { ...base, background: "#BFEF90", color: "#43890F" };
		case "parked":
		case "paused":
			return { ...base, background: "#E6E6E6", color: "#454545", border: "1px solid #D3D3D3" };
		case "cancelled":
		case "canceled":
			return { ...base, background: "#D3D3D3", color: "#454545" };
		default:
			return { ...base, background: "#f0f0f0", color: "#666" };
	}
};

export const formatLastModified = (date: string) => {
	if (!date) return "-";

	try {
		const d = new Date(date);
		if (isNaN(d.getTime())) return date;
		return d.toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });
	} catch {
		return date;
	}
};

// Refactored searchIn
export const searchIn = (text: string | undefined, lowerTerm: string): number => {
	if (typeof text !== "string") return 0;
	const lowerText = text.toLowerCase();

	if (!lowerTerm) return 1;

	// 1. Exact match
	if (lowerText === lowerTerm) return 100;

	// 2. Full Substring match
	if (lowerText.includes(lowerTerm)) {
		return lowerText.startsWith(lowerTerm) ? 80 : 60;
	}

	// 3. Tokenized matching (Handling multi-word search terms)
	const textTokens = lowerText.split(/[^a-z0-9]+/).filter(Boolean);
	const termTokens = lowerTerm.split(/[^a-z0-9]+/).filter(Boolean);

	if (!textTokens.length || !termTokens.length) return 0;

	// If there are multiple words in the search term, evaluate them collectively
	if (termTokens.length > 1) {
		let totalScore = 0;
		let allTermsMatched = true;

		for (const tTerm of termTokens) {
			let bestMatchForTerm = 0;
			// Check if this specific search word exists in ANY of the text tokens
			for (const tText of textTokens) {
				if (tText === tTerm) bestMatchForTerm = Math.max(bestMatchForTerm, 90);
				else if (tText.startsWith(tTerm)) bestMatchForTerm = Math.max(bestMatchForTerm, 70);
				else if (tText.includes(tTerm)) bestMatchForTerm = Math.max(bestMatchForTerm, 50);
			}
			if (bestMatchForTerm === 0) {
				allTermsMatched = false;
				break; // Missing one word means it's not a great match for multi-word
			}
			totalScore += bestMatchForTerm;
		}

		if (allTermsMatched) {
			// Average the scores of the matched words
			return totalScore / termTokens.length;
		}
	}

	const maxDistance = lowerTerm.length <= 4 ? 1 : 2;

	const boundedLevenshtein = (a: string, b: string, max: number) => {
		const aLen = a.length;
		const bLen = b.length;
		if (Math.abs(aLen - bLen) > max) return max + 1;

		let prev = new Array(bLen + 1);
		let curr = new Array(bLen + 1);

		for (let j = 0; j <= bLen; j += 1) prev[j] = j;

		for (let i = 1; i <= aLen; i += 1) {
			curr[0] = i;
			let rowMin = curr[0];

			for (let j = 1; j <= bLen; j += 1) {
				const cost = a[i - 1] === b[j - 1] ? 0 : 1;
				const deletion = prev[j] + 1;
				const insertion = curr[j - 1] + 1;
				const substitution = prev[j - 1] + cost;
				const value = Math.min(deletion, insertion, substitution);
				curr[j] = value;
				if (value < rowMin) rowMin = value;
			}

			if (rowMin > max) return max + 1;
			[prev, curr] = [curr, prev];
		}

		return prev[bLen];
	};

	let bestTokenScore = 0;
	for (const token of textTokens) {
		let score = 0;
		if (token === lowerTerm) {
			score = 90;
		} else if (token.startsWith(lowerTerm)) {
			score = 70;
		} else if (token.includes(lowerTerm)) {
			score = 50;
		} else if (token[0] === lowerTerm[0] && Math.abs(token.length - lowerTerm.length) <= maxDistance) {
			const dist = boundedLevenshtein(token, lowerTerm, maxDistance);
			if (dist <= maxDistance) {
				// Typo score based on distance
				score = 40 - dist * 10;
			}
		}
		if (score > bestTokenScore) bestTokenScore = score;
	}

	return bestTokenScore;
};

export const searchArr = (arr: string[] | undefined, term: string) => {
	if (!arr?.length) return 0;
	let max = 0;
	for (const i of arr) {
		const score = typeof i === "string" ? searchIn(i, term) : 0;
		if (score > max) max = score;
	}
	return max;
};

export const searchUsers = (users: CampaignUser[] | undefined, term: string) => {
	if (!users?.length) return 0;
	let max = 0;
	for (const u of users) {
		const score = searchIn(u?.name, term);
		if (score > max) max = score;
	}
	return max;
};

export const filterCampaigns = (campaigns: CampaignData[], searchText: string) => {
	const lowerTerm = searchText.trim().toLowerCase();
	if (!lowerTerm) return campaigns;

	const scored = campaigns
		.map((campaign) => {
			const scores = [
				searchIn(campaign.name, lowerTerm) * 1.5, // Name matches are most important
				searchIn(campaign.projectCode, lowerTerm),
				searchIn(campaign.statusPhase, lowerTerm) * 0.5,
				searchIn(campaign.type, lowerTerm) * 0.5,
				searchArr(campaign.client, lowerTerm),
				searchArr(campaign.brand, lowerTerm),
				searchArr(campaign.location, lowerTerm),
				searchArr(campaign.amplifyTeam, lowerTerm),
				searchUsers(campaign.lead, lowerTerm),
				searchUsers(campaign.support, lowerTerm),
				searchUsers(campaign.accountManager, lowerTerm),
				searchIn(campaign.entityId, lowerTerm),
			];
			return { campaign, score: Math.max(...scores) };
		})
		.filter((item) => item.score > 0);

	return scored.sort((a, b) => b.score - a.score).map((item) => item.campaign);
};
